/**
 * Feature #2598 - Refactor Frontend init 重構前端 init 邏輯
 * An adapter relays init data to existing code without large scale refactoring
 * @see https://flredmine.herokuapp.com/issues/2598 (Frontend)
 * @see https://flredmine.herokuapp.com/issues/2601 (Backend)
 */

import FunlidayCookie from './funlidayCookie';

const initAdapter = {};
const LANG_WHITELIST = ['en', 'ja', 'ko', 'zh_tw', 'zh_cn', 'zh_hk'];
const REGION_WHITELIST = ['tw', 'cn', 'hk', 'jp', 'kr', 'en'];

/**
 * Adapter read/write values from server inserted HTML meta tag
 *
 * @param {string} buff - base64 string from server inserted into html
 * @description
 * 1. Parse base64 string from server inserted into html
 * 2. Inject key, values pair into initAdapter{}
 * 3. call parseFunlidayCookies() to inject cookies into initAdapter{},
 * 4. call writeLocaleSettings(), writeWebToken(), writePoibankToken()
 */
export const parseInitBuffer = buff => {
  const b = Buffer.from(buff, 'base64').toString();
  JSON.parse(b).forEach(({ name, value }) => {
    initAdapter[name] = value;
  });

  parseFunlidayCookies();
  writeLocaleSettings();
  writeWebToken();
  writePoibankToken();
  writeMemberId();
  writeAccessToken();
};

const parseFunlidayCookies = () => {
  const language = FunlidayCookie.get('language');
  const region = FunlidayCookie.get('region');
  const webToken = FunlidayCookie.get('webToken');
  const poibankToken = FunlidayCookie.get('poibankToken');
  if (language && !initAdapter.language) {
    if (language.indexOf(LANG_WHITELIST)) {
      initAdapter.language = language;
    }
  }
  if (initAdapter.language) {
    initAdapter.locale = initAdapter.language.replace('_', '-');
  }
  if (region && !initAdapter.region) {
    if (region.indexOf(REGION_WHITELIST)) {
      initAdapter.region = region;
    }
  }
  if (webToken && !initAdapter.webToken) {
    initAdapter.webToken = webToken;
  }
  if (poibankToken && !initAdapter.poibankToken) {
    initAdapter.poibankToken = poibankToken;
  }
};

/**
 * Write Locale settings into Cookies
 *
 * @param {string} locale - default: `initAdapter.language`
 * @param {string} region - default: `initAdapter.region`
 *
 */
export const writeLocaleSettings = (
  locale = initAdapter.language,
  region = initAdapter.region
) => {
  const storedLanguage = FunlidayCookie.get('language');
  const storedRegion = FunlidayCookie.get('region');

  if (locale && !storedLanguage) {
    FunlidayCookie.set('language', locale.replace('-', '_'));
  }
  if (region && !storedRegion) {
    FunlidayCookie.set('region', region);
  }
};

/**
 * Write web token into localStorage
 *
 * @param {string} webToken - default: `initAdapter.webToken`
 *
 */
export const writeWebToken = (webToken = initAdapter.webToken) => {
  if (webToken) {
    localStorage?.setItem('webToken', JSON.stringify(webToken));
  }
};

/**
 * Write web token into localStorage
 *
 * @param {string} poibankToken - default: `initAdapter.poibankToken`
 * Note!: 'poibankToken' is styled as 'poiBankToken' in localstorage
 */
export const writePoibankToken = (poibankToken = initAdapter.poibankToken) => {
  if (poibankToken) {
    localStorage?.setItem('poiBankToken', poibankToken.token);
  }
};

/**
 * Write memberId into Cookies if member object existed in local storage
 */
const writeMemberId = () => {
  const checkMemberId = () => {
    const memberObj = localStorage?.getItem('member');
    try {
      const member = JSON.parse(memberObj);
      return member._id;
    } catch (e) {
      return null;
    }
  };

  const memberId = checkMemberId();
  if (memberId && !FunlidayCookie.get('memberId')) {
    FunlidayCookie.set('memberId', memberId);
    FunlidayCookie.set('no-oauth-prompt', '1');
  }
};

/**
 * Write accessToken into Cookies if accessToken object existed in local storage
 */
const writeAccessToken = () => {
  const checkToken = () => {
    const accessTokenObj = localStorage?.getItem('accessToken');
    try {
      const accessToken = JSON.parse(accessTokenObj);
      return accessToken.token;
    } catch (e) {
      return null;
    }
  };

  const cachedToken = checkToken();
  if (cachedToken) {
    FunlidayCookie.set('accessToken', cachedToken);
  }
};

export default initAdapter;
